import { useDatabaseContent } from '@databases/database-state';
import { DatabaseJSON } from '@databases/model/database';
import { FieldType } from '@databases/model/field';
import { appStoreUtils } from '@databases/redux/store';
import { selectCollabManagerByContentId } from '@databases/utils/redux/collab/selectors';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import useView from '@databases/views/useView';
import { useEffect } from 'react';

import { useAnalytics } from './useAnalytics';
import { getDatabaseContainerType } from './utils';

/**
 * Tracks database metrics for the current database.
 * This will fire whenever the database is loaded and ready or when the contentId changes.
 */
export const useTrackDatabaseMetrics = () => {
  const { contentId } = useDatabaseContent() as { contentId: string };
  const { view } = useView();
  const analytics = useAnalytics();

  const manager = appStoreUtils.useSelector((state) =>
    selectCollabManagerByContentId(state, { contentId })
  );

  const databaseStatus = appStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  function getEntriesCount(database: DatabaseJSON) {
    return database.entries.length;
  }

  function getFieldsCount(database: DatabaseJSON) {
    return database.fields.length;
  }

  function getViewsCount(database: DatabaseJSON) {
    return database.views.length;
  }

  function getFieldTypeCounts(database: DatabaseJSON) {
    const fieldTypeCounts = new Map<FieldType, number>();

    Object.keys(database.fieldsById).forEach((id) => {
      const type = database.fieldsById[id].type;

      if (fieldTypeCounts.has(type)) {
        fieldTypeCounts.set(type, (fieldTypeCounts.get(type) ?? 0) + 1);
      } else {
        fieldTypeCounts.set(type, 1);
      }
    });

    return Object.fromEntries(fieldTypeCounts) as Record<FieldType, number>;
  }

  function getFieldTypes(database: DatabaseJSON) {
    return Array.from(new Set(Object.keys(getFieldTypeCounts(database)))) as FieldType[];
  }

  function getViewSettings() {
    return {
      layoutType: view.settings.layout.type,
      showNumberedEntriesEnabled: view.settings.layout.showNumberedEntries,
      cardWidth: view.settings.layout.cardWidth,
    };
  }

  useEffect(() => {
    if (
      !contentId ||
      databaseStatus?.status !== AsyncOperationStatus.SUCCEEDED ||
      !manager
    ) {
      return;
    }

    const fire = () => {
      const jsonData = manager.database.toJSON();

      analytics.sendDatabaseProfiledEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        attributes: {
          fieldsCount: getFieldsCount(jsonData),
          fieldTypeCounts: getFieldTypeCounts(jsonData),
          fieldTypes: getFieldTypes(jsonData),
          entriesCount: getEntriesCount(jsonData),
          viewsCount: getViewsCount(jsonData),
          viewSettings: getViewSettings(),
        },
      });
    };

    fire();

    // this effect should only run when the database is loaded and ready or when the contentId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseStatus?.status, contentId, manager]);
};
